import type {UseTourOperatorDiscountCampaignsContext} from '@/modules/common/hooks/useCampaigns';
import type {TourOperatorDiscountCampaign} from '@/modules/common/hooks/useCampaigns/types';
import type {Breakpoint as BreakpointType} from '@travel-frontend/themes';

import {
  Breakpoint,
  useBreakpoint,
  useBreakpointDown,
} from '@travel-frontend/core';
import {
  DiscountInterceptor,
  DiscountInterceptorSize,
} from '@travel-frontend/ui-components';

import {CampaignBannersHintElement} from '@/components/CampaignBanners';
import {Background} from '@/components/CampaignBanners/components/Background';
import {Badge} from '@/components/TourOperatorDiscountCampaignBanner/components/Badge';
import {BadgeText} from '@/components/TourOperatorDiscountCampaignBanner/components/BadgeText';
import {Container} from '@/components/TourOperatorDiscountCampaignBanner/components/Container';
import {CtaBadge} from '@/components/TourOperatorDiscountCampaignBanner/components/CtaBadge';
import {Discount} from '@/components/TourOperatorDiscountCampaignBanner/components/Discount';
import {SubTitle} from '@/components/TourOperatorDiscountCampaignBanner/components/SubTitle';
import {TextWrapper} from '@/components/TourOperatorDiscountCampaignBanner/components/TextWrapper';
import {Title} from '@/components/TourOperatorDiscountCampaignBanner/components/Title';

type Props = TourOperatorDiscountCampaign & {
  onClick: UseTourOperatorDiscountCampaignsContext['handleTourOperatorDiscountCampaignBannerClick'];
};

export const TourOperatorDiscountCampaignBanner: React.FunctionComponent<
  Props
> = ({
  onClick,
  gradient,
  hint,
  interceptorContent,
  subtitles,
  titles,
  badgeContent = '',
  hintTooltip,
  ...restProps
}) => {
  const mdAndLg = useBreakpoint(['md', 'lg']);
  const smDown = useBreakpointDown('sm');
  const lgDown = useBreakpointDown('lg');

  const titleElements = (
    ['xs', 'sm', 'md', 'lg', 'xl'] as Array<BreakpointType>
  ).map((bp) => (
    <Breakpoint key={bp} matches={[bp]}>
      <Title>{titles[bp]}</Title>
      <SubTitle>{subtitles[bp]}</SubTitle>
    </Breakpoint>
  ));

  const [from, to] = gradient.split(',');

  let badge: React.ReactNode = badgeContent;

  const priceOrPercentageBadgeContentMatch = /\d{2,3}.€|\d{2,3}.%/u.exec(
    badgeContent
  );
  if (priceOrPercentageBadgeContentMatch) {
    const [before, after] = badgeContent.split(
      priceOrPercentageBadgeContentMatch[0]
    );
    badge = (
      <>
        {before} <b>{priceOrPercentageBadgeContentMatch}</b> {after}
      </>
    );
  }

  const interceptorSize = lgDown
    ? DiscountInterceptorSize.SMALL
    : DiscountInterceptorSize.MEDIUM;

  return (
    <Container
      as="button"
      gradient={gradient}
      onClick={() =>
        onClick({
          gradient,
          hint,
          interceptorContent,
          subtitles,
          titles,
          badgeContent,
          hintTooltip,
          ...restProps,
        })
      }
      direction="row"
      alignItems="center"
      spacing="none"
      justifyContent="space-between"
    >
      <Background from={from} to={to} />
      <Discount>
        <DiscountInterceptor
          size={interceptorSize}
          text={interceptorContent.text}
          highlight={interceptorContent.highlight}
          highlightTextColor={to}
        />
        {smDown && (
          <CampaignBannersHintElement hint={hint} hintTooltip={hintTooltip} />
        )}
      </Discount>
      <TextWrapper alignItems="center" justifyContent="center" spacing="small">
        {titleElements}
        {mdAndLg && (
          <CampaignBannersHintElement hint={hint} hintTooltip={hintTooltip} />
        )}
      </TextWrapper>
      {smDown && <BadgeText>{badge}</BadgeText>}
      {!lgDown && (
        <Badge>
          <CtaBadge>{badge}</CtaBadge>
          <CampaignBannersHintElement hint={hint} hintTooltip={hintTooltip} />
        </Badge>
      )}
    </Container>
  );
};
